import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Grid,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import { Search as SearchIcon, RefreshCw, Eye, User, Trash2, Mail, MessageCircle, Plus, FileText as QuotesLogoIcon } from 'lucide-react';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const QUOTE_STATUS_LABELS = {
  draft: 'Lead',
  sent: 'Prospect',
  pending: 'Prospect',
  accepted: 'Cliente',
  rejected: 'Perso'
};

export default function QuotesList() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [stats, setStats] = useState({ draft: 0, sent: 0, accepted: 0, rejected: 0 });
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [quoteDetailsLoading, setQuoteDetailsLoading] = useState(false);
  const [quoteDialogTab, setQuoteDialogTab] = useState(0);
  
  // Pagination & Sorting state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order,
        search,
        status: filterStatus,
        dateFilter
      };
      
      const response = await api.get('/quotes', { params });
      
      if (response.data && response.data.meta) {
        setQuotes(response.data.data);
        setTotalCount(response.data.meta.total);
      } else if (Array.isArray(response.data)) {
        // Fallback for old API structure
        setQuotes(response.data);
        setTotalCount(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching quotes list:', error);
      toast.error('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = { dateFilter };
      const res = await api.get('/quotes/stats', { params });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchQuotes();
    fetchStats();
  }, [page, rowsPerPage, orderBy, order, filterStatus, dateFilter]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchQuotes();
    fetchStats();
  };

  // Search handling
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      setPage(0); // Reset to first page on new search
      fetchQuotes();
    }
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
    setPage(0);
  };

  const handleWidgetClick = (status) => {
    if (filterStatus === status) {
      setFilterStatus('all');
    } else {
      setFilterStatus(status);
    }
    setPage(0); // Reset to first page
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleViewContact = (quote) => {
    if (quote.contact_id) {
      navigate(`/contacts/${quote.contact_id}`);
    }
  };

  const handleDeleteQuote = async (quote) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
    try {
      await api.delete(`/quotes/${quote.id}`);
      toast.success('Preventivo eliminato correttamente');
      fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      const message =
        error?.response?.data?.message || "Errore nell'eliminazione del preventivo";
      toast.error(message);
    }
  };

  const updateQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      toast.success(`Stato aggiornato a ${QUOTE_STATUS_LABELS[status]}`);
      fetchQuotes();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleSendEmail = async (quote) => {
    if (!quote.email) {
      toast.warning('Nessuna email disponibile per questo contatto');
      return;
    }
    const subject = `Preventivo #${quote.id}`;
    const body = `Gentile ${quote.display_name},\n\nin allegato il preventivo richiesto.`;
    window.open(`mailto:${quote.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    
    if (window.confirm('Hai inviato la email? Vuoi aggiornare lo stato del preventivo a "Inviato"?')) {
      await updateQuoteStatus(quote.id, 'sent');
    }
  };

  const handleSendWhatsapp = async (quote) => {
    if (!quote.phone) {
      toast.warning('Nessun numero di telefono disponibile per questo contatto');
      return;
    }
    const cleanPhone = quote.phone.replace(/[^0-9]/g, '');
    const text = `Gentile ${quote.display_name}, ecco il preventivo richiesto: ...`; 
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`, '_blank');

    if (window.confirm('Hai inviato il messaggio WhatsApp? Vuoi aggiornare lo stato del preventivo a "Inviato"?')) {
      await updateQuoteStatus(quote.id, 'sent');
    }
  };

  const handleViewQuoteDetails = async (quote) => {
    setQuoteDialogTab(0);
    setOpenQuoteDialog(true);
    setQuoteDetailsLoading(true);
    setQuoteDetails(null);
    try {
      const response = await api.get(`/quotes/${quote.id}`);
      const data = response.data;
      if (Array.isArray(data.items)) {
        data.items = data.items.map((item) => ({
          ...item,
          debt_position: {
            controparte_name: item.controparte_name,
            tipo_rapporto_name: item.tipo_rapporto_label
          }
        }));
      }
      setQuoteDetails(data);
    } catch (error) {
      console.error('Error fetching quote details:', error);
      toast.error('Errore nel caricamento dei dettagli del preventivo');
      setOpenQuoteDialog(false);
    } finally {
      setQuoteDetailsLoading(false);
    }
  };

  const handleQuoteDialogTabChange = (event, newValue) => {
    setQuoteDialogTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <QuotesLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Preventivi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Elenco preventivi generati con stato e canali di invio
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={() => navigate('/quotes/new')}
        >
          Nuovo Preventivo
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', 
              borderRadius: 2,
              cursor: 'pointer',
              border: filterStatus === 'draft' ? '2px solid' : 'none',
              borderColor: 'text.secondary',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => handleWidgetClick('draft')}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography color="text.secondary" gutterBottom variant="overline" sx={{ fontWeight: 600 }}>
                  LEAD
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.draft}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'action.selected', borderRadius: '50%', color: 'text.primary', display: 'flex' }}>
                <EditNoteIcon />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', 
              borderRadius: 2,
              cursor: 'pointer',
              border: filterStatus === 'sent' ? '2px solid' : 'none',
              borderColor: 'info.main',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => handleWidgetClick('sent')}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography color="text.secondary" gutterBottom variant="overline" sx={{ fontWeight: 600 }}>
                  PROSPECT
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.sent}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'info.main', borderRadius: '50%', color: 'white', display: 'flex' }}>
                <SendIcon />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', 
              borderRadius: 2,
              cursor: 'pointer',
              border: filterStatus === 'accepted' ? '2px solid' : 'none',
              borderColor: 'success.main',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => handleWidgetClick('accepted')}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography color="text.secondary" gutterBottom variant="overline" sx={{ fontWeight: 600 }}>
                  CLIENTE
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.accepted}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'success.main', borderRadius: '50%', color: 'white', display: 'flex' }}>
                <CheckCircleIcon />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', 
              borderRadius: 2,
              cursor: 'pointer',
              border: filterStatus === 'rejected' ? '2px solid' : 'none',
              borderColor: 'error.main',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => handleWidgetClick('rejected')}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography color="text.secondary" gutterBottom variant="overline" sx={{ fontWeight: 600 }}>
                  PERSO
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.rejected}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'error.main', borderRadius: '50%', color: 'white', display: 'flex' }}>
                <CancelIcon />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Cerca per ID, nome cliente, stato... (Premi Invio)"
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchSubmit}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Periodo</InputLabel>
            <Select
              value={dateFilter}
              label="Periodo"
              onChange={handleDateFilterChange}
            >
              <MenuItem value="all">Tutto</MenuItem>
              <MenuItem value="today">Oggi</MenuItem>
              <MenuItem value="yesterday">Ieri</MenuItem>
              <MenuItem value="last_7_days">Ultimi 7 giorni</MenuItem>
              <MenuItem value="last_30_days">Ultimi 30 giorni</MenuItem>
              <MenuItem value="this_month">Mese corrente</MenuItem>
              <MenuItem value="last_month">Mese scorso</MenuItem>
              <MenuItem value="this_year">Anno corrente</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={handleRefresh}>
            <RefreshCw size={18} />
          </IconButton>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : quotes.length === 0 && !loading ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="textSecondary">Nessun preventivo trovato</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'id' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'created_at' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'created_at'}
                      direction={orderBy === 'created_at' ? order : 'asc'}
                      onClick={() => handleRequestSort('created_at')}
                    >
                      Data Creazione
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'display_name' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'display_name'}
                      direction={orderBy === 'display_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('display_name')}
                    >
                      Cliente / Soggetti
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >
                      Stato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'total_debt_amount' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'total_debt_amount'}
                      direction={orderBy === 'total_debt_amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('total_debt_amount')}
                    >
                      Totale Debito
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'total_fee_amount' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'total_fee_amount'}
                      direction={orderBy === 'total_fee_amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('total_fee_amount')}
                    >
                      Totale Compenso
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'created_by_name' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'created_by_name'}
                      direction={orderBy === 'created_by_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('created_by_name')}
                    >
                      Referente Commerciale
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    Azienda
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map((quote) => (
                <TableRow key={quote.id} hover>
                  <TableCell>#{quote.id}</TableCell>
                  <TableCell>
                    {quote.created_at ? new Date(quote.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {quote.display_name || '-'}
                    </Typography>
                    {Number(quote.co_parties_count || 0) > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        +{Number(quote.co_parties_count)} co-intestatari
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={QUOTE_STATUS_LABELS[quote.status] || quote.status}
                      size="small"
                      color={
                        quote.status === 'accepted'
                          ? 'success'
                          : quote.status === 'sent'
                          ? 'info'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    € {parseFloat(quote.total_debt_amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    € {parseFloat(quote.total_fee_amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>{quote.created_by_name || '-'}</TableCell>
                  <TableCell>
                    {quote.company_name_assigned || '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      title="Dettaglio preventivo"
                      size="small"
                      onClick={() => handleViewQuoteDetails(quote)}
                    >
                      <Eye size={18} />
                    </IconButton>
                    {quote.contact_id && (
                      <IconButton
                        title="Apri scheda contatto"
                        size="small"
                        onClick={() => handleViewContact(quote)}
                      >
                        <User size={18} />
                      </IconButton>
                    )}
                    {quote.status === 'draft' && (
                      <>
                        <IconButton
                          title="Invia via Email"
                          size="small"
                          color="primary"
                          onClick={() => handleSendEmail(quote)}
                        >
                          <Mail size={18} />
                        </IconButton>
                        <IconButton
                          title="Invia via WhatsApp"
                          size="small"
                          color="success"
                          onClick={() => handleSendWhatsapp(quote)}
                        >
                          <MessageCircle size={18} />
                        </IconButton>
                        <IconButton
                          title="Elimina preventivo"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteQuote(quote)}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina"
        />
        </>
      )}
      <Dialog
        open={openQuoteDialog}
        onClose={() => setOpenQuoteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {quoteDetails ? `Dettagli Preventivo #${quoteDetails.id}` : 'Dettagli Preventivo'}
        </DialogTitle>
        <DialogContent>
          {quoteDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : quoteDetails ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tabellario applicato:{' '}
                <strong>{quoteDetails.fee_config?.name || 'N/A'}</strong>
              </Typography>

              {quoteDetails.company_name_assigned && (
                <Typography variant="subtitle1" gutterBottom>
                  Contratto con:{' '}
                  <strong>{quoteDetails.company_name_assigned}</strong>
                </Typography>
              )}

              <Tabs
                value={quoteDialogTab}
                onChange={handleQuoteDialogTabChange}
                sx={{ mt: 2, mb: 2 }}
              >
                <Tab label="Posizioni" />
                <Tab label="Coobbligati / Garanti" />
                <Tab label="Piano Rateale" />
              </Tabs>

              {quoteDialogTab === 2 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Modalità di Pagamento: <strong>{quoteDetails.payment_mode || 'Non specificata'}</strong>
                  </Typography>
                  
                  {quoteDetails.installments && quoteDetails.installments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Scadenza</TableCell>
                            <TableCell align="right">Importo</TableCell>
                            <TableCell>Metodo</TableCell>
                            <TableCell>Note</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {quoteDetails.installments.map((inst, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {inst.due_date ? new Date(inst.due_date).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell align="right">
                                € {parseFloat(inst.amount).toFixed(2)}
                              </TableCell>
                              <TableCell>{inst.payment_method || '-'}</TableCell>
                              <TableCell>{inst.note || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Nessun piano rateale definito per questo preventivo.
                    </Typography>
                  )}
                </Box>
              )}

              {quoteDialogTab === 1 && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  {quoteDetails.parties && quoteDetails.parties.length > 0 ? (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Soggetti del Preventivo
                      </Typography>
                      {quoteDetails.parties.map((party) => (
                        <Box
                          key={party.id || `${party.contact_id}-${party.role}`}
                          sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ mr: 1, fontWeight: party.is_primary ? 600 : 400 }}
                          >
                            {party.is_primary ? 'Intestatario' : 'Co-intestatario'}:
                          </Typography>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {party.display_name ||
                              party.contact_name ||
                              `Contatto #${party.contact_id}`}
                          </Typography>
                          <Chip
                            label={party.role || 'ALTRO'}
                            size="small"
                            color={party.is_primary ? 'primary' : 'default'}
                          />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nessun coobbligato o garante associato a questo preventivo.
                    </Typography>
                  )}
                </Box>
              )}

              {quoteDialogTab === 0 && (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Posizione</TableCell>
                          <TableCell align="right">Debito</TableCell>
                          <TableCell align="right">Compenso</TableCell>
                          <TableCell>Regola Applicata</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {quoteDetails.items &&
                          quoteDetails.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {item.debt_position?.controparte_name || 'N/A'} -{' '}
                                {item.debt_position?.tipo_rapporto_name || 'N/A'}
                              </TableCell>
                              <TableCell align="right">
                                € {parseFloat(item.debt_amount).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                € {parseFloat(item.fee_amount).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {item.fee_rule_used
                                  ? item.fee_rule_used.tipo_rapporto_label ||
                                    'Regola specifica'
                                  : item.fee_rule_description || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5' }}>
                          <TableCell>
                            <strong>TOTALE</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>
                              €{' '}
                              {quoteDetails.total_debt_amount
                                ? parseFloat(quoteDetails.total_debt_amount).toFixed(2)
                                : parseFloat(quoteDetails.total_debt || 0).toFixed(2)}
                            </strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>
                              €{' '}
                              {quoteDetails.total_fee_amount
                                ? parseFloat(quoteDetails.total_fee_amount).toFixed(2)
                                : parseFloat(quoteDetails.total_fee || 0).toFixed(2)}
                            </strong>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          ) : (
            <Typography>Impossibile caricare i dettagli del preventivo.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQuoteDialog(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
